import {
	bookingResource,
	checkoutResource,
	validationErrorResource,
	type BookingResource,
	type CheckoutResource,
	type ValidationErrorResource,
	type AddressLookupResource,
	addressLookupResource,
	productFilterResource,
	type ProductFilterResource,
} from '@/api/resources';
import { cancelPendingRequest, get, post, put, del } from '@/api/client';
import { createBookingRequest, type CreateCartRequest, type CreateBookingRequest } from '@/api/requests';
import type { AxiosResponse } from 'axios';
import { z } from 'zod';

/**
 * Create or get an existing new checkout.
 *
 * The expensive upsell groups are skipped by default (include_upsells=0) so the initial load
 * stays fast; they are fetched separately via getCheckout() when the choice steps are entered.
 */
export async function createCheckout(data: CreateCartRequest, includeUpsells = false) {
	const response = await post<CheckoutResource>('checkout', data, undefined, upsellParams(includeUpsells));

	return await tryParseResult<checkoutResource>(response, checkoutResource);
}

/**
 * Update an existing checkout.
 *
 * Skips the expensive upsell groups by default so date/time switches stay fast.
 */
export async function updateCheckout(id: string, data: CheckoutResource, includeUpsells = false) {
	const signal = cancelPendingRequest('checkout.updateCheckout');
	const response = await put<CheckoutResource>(`checkout/${id}`, data.data, signal, upsellParams(includeUpsells));

	return await tryParseResult<checkoutResource>(response, checkoutResource);
}

/**
 * Fetch an existing checkout including the expensive upsell groups (treatments, rituals, etc).
 * Used to lazy-load upsells when the user enters the choice steps.
 */
export async function getCheckout(id: string) {
	const signal = cancelPendingRequest('checkout.getCheckout');
	const response = await get<CheckoutResource>(`checkout/${id}`, { include_upsells: 1 }, signal);

	return await tryParseResult<checkoutResource>(response, checkoutResource);
}

/**
 * Query params controlling whether the backend computes the expensive upsell groups.
 * Omitted when including (backend defaults to include) to keep URLs clean.
 */
function upsellParams(includeUpsells: boolean): { include_upsells?: number } {
	return includeUpsells ? {} : { include_upsells: 0 };
}

/**
 * Create a new booking.
 */
export async function createBooking(id: string, data: CreateBookingRequest) {
	const requestData = createBookingRequest.safeParse(data);
	if (!requestData.success) {
		return Promise.reject(requestData.error);
	}

	const signal = cancelPendingRequest('checkout.createBooking');
	const response = await post<BookingResource>(`checkout/${id}/book`, data, signal);

	return await tryParseResult<bookingResource>(response, bookingResource);
}

/**
 * Add person choice
 */
export async function setPersonChoices(checkout_id: string, person_ids: number[], slot: string, product_id: number) {
	const signal = cancelPendingRequest(`checkout.setPersonChoices.${product_id}`);
	const response = await put<CheckoutResource>(
		`checkout/${checkout_id}/choices/${slot}/product/${product_id}/persons`,
		{ person_ids },
		signal,
	);

	return await tryParseResult<checkoutResource>(response, checkoutResource);
}

/**
 * Add person choice
 */
export async function setPersonChoice(checkout_id: string, person_id: number, slot: string, product_id: number) {
	const signal = cancelPendingRequest(`checkout.setPersonChoice.${product_id}`);
	const response = await put<CheckoutResource>(
		`checkout/${checkout_id}/choices/${slot}/product/${product_id}/person/${person_id}`,
		{},
		signal,
	);

	return await tryParseResult<checkoutResource>(response, checkoutResource);
}

/**
 * Delete person choice
 */
export async function deletePersonChoice(checkout_id: string, person_id: number, slot: string, product_id: number) {
	const signal = cancelPendingRequest(`checkout.deletePersonChoice.${product_id}`);
	const response = await del<CheckoutResource>(
		`checkout/${checkout_id}/choices/${slot}/product/${product_id}/person/${person_id}`,
		{},
		signal,
	);

	return await tryParseResult<checkoutResource>(response, checkoutResource);
}

/**
 * Create or get an existing new checkout.
 */
export async function validateCheckoutStep(checkout_id: string, step: string) {
	const signal = cancelPendingRequest('checkout.validateStep');
	const responseData = (
		await get<ValidationErrorResource>(`checkout/${checkout_id}/validate/${step}`, undefined, signal)
	).data;
	const result = validationErrorResource.safeParse(responseData);
	return result.success ? responseData.errors : null;
}

/**
 * Get address info
 */
export async function lookupAddress(zipCode: string, houseNumber: number, houseNumberAddition?: string) {
	const signal = cancelPendingRequest('checkout.lookupAddress');
	const response = await get<AddressLookupResource>(
		`checkout/address/lookup`,
		{ zipCode, houseNumber, houseNumberAddition },
		signal,
	);

	return await tryParseResult<addressLookupResource>(response, addressLookupResource);
}

/**
 * Get product filter options
 */
export async function getProductFilters(slot: string) {
	const signal = cancelPendingRequest('checkout.getProductFilters');
	const response = await get<ProductFilterResource>(`checkout/filters/${slot}`, undefined, signal);

	return await tryParseResult<productFilterResource>(response, productFilterResource);
}

function tryParseResult<T>(
	response: AxiosResponse,
	schema: z.ZodType<T>,
): Promise<never | void | z.ZodError<T> | Awaited<T>> {
	if (!isSuccess(response)) {
		return Promise.reject(response.data);
	}

	if (response.status === 204 || response.data === null || response.data === undefined) {
		return Promise.resolve();
	}

	const result = schema.safeParse(response.data);

	if (!result.success) {
		return Promise.reject(result.error);
	}

	return Promise.resolve(result.data);
}

function isSuccess(response: AxiosResponse) {
	return response.status >= 200 && response.status < 300;
}

export default {
	createBooking,
	setPersonChoices,
	deletePersonChoice,
	getProductFilters,
};
